<template lang="pug">
    v-dialog(v-model="dialogOpen" content-class="haq-dialog" max-width="680" persistent scrollable)
        v-card.haq-dialog-card.schedule-modal
            v-card-title
                span {{ isEditing ? 'Edit Scheduled Report' : 'Schedule Report' }}
                v-spacer
                v-btn(icon @click="close")
                    v-icon mdi-close
            v-card-text
                v-form.schedule-form(ref="form" v-model="formValid")
                    .schedule-status
                        v-switch(v-model="form.isActive" label="Active" hide-details)
                        v-btn.haq-btn.haq-btn-secondary(:loading="sendingTest" :disabled="!canSendTest || sendingTest" @click="sendTest") Send test to me
                    //- Report Name
                    HaqTextField(
                        v-model="form.name"
                        label="Report Name"
                        :rules="[v => !!v || 'Name is required']"
                    )
                    
                    //- Timeline Display (readonly when creating)
                    HaqTextField(
                        v-if="!isEditing"
                        :value="timelineLabel"
                        label="Time Period"
                        readonly
                    )
                    
                    //- Timeline Selection (when editing)
                    HaqSelect(
                        :menu-props="selectMenuProps"
                        v-else
                        v-model="form.timeline"
                        :items="timelineOptions"
                        item-text="label"
                        item-value="value"
                        label="Time Period"
                        :rules="[v => !!v || 'Time period is required']"
                    )
                    
                    .included-section
                        .included-heading
                            .section-label Included devices / zones and points ({{ reportPoints.length }})
                            v-btn.haq-btn.haq-btn-secondary(:disabled="loadingContents || !!contentsError" @click="pointSelectorOpen = true") Edit points
                        v-progress-linear(v-if="loadingContents" indeterminate)
                        .contents-error(v-else-if="contentsError")
                            span {{ contentsError }}
                            v-btn(text small @click="loadContents") Retry
                        .included-list(v-else)
                            .included-point(v-for="point in reportPoints" :key="point.id")
                                span {{ point.zoneName || 'Site' }}
                                strong {{ point.name || point.id }}
                                span(v-if="point.unavailable") Unavailable
                                v-btn(icon small :aria-label="'Remove ' + (point.name || point.id)" @click="removePoint(point.id)")
                                    v-icon(small) mdi-close
                        p(v-if="!loadingContents && !contentsError && !reportPoints.length") Select at least one point.

                    //- Schedule Frequency & Timing
                    .schedule-timing-section
                        .section-label Delivery Schedule
                        v-row
                            v-col(:cols="12" :sm="6")
                                HaqSelect(
                                    :menu-props="selectMenuProps"
                                    v-model="form.frequency"
                                    :items="frequencyOptions"
                                    item-text="label"
                                    item-value="value"
                                    label="Frequency"
                                    hide-details
                                )
                            v-col(:cols="12" :sm="6")
                                HaqSelect(
                                    :menu-props="selectMenuProps"
                                    v-model="form.sendTime"
                                    :items="sendTimeOptions"
                                    item-text="label"
                                    item-value="value"
                                    label="Send Time"
                                    hide-details
                                )
                        v-row(v-if="form.frequency === 'weekly'")
                            v-col(:cols="12" :sm="6")
                                HaqSelect(
                                    :menu-props="selectMenuProps"
                                    v-model="form.sendDay"
                                    :items="dayOfWeekOptions"
                                    item-text="label"
                                    item-value="value"
                                    label="Day of Week"
                                    hide-details
                                )
                        v-row(v-if="form.frequency === 'monthly'")
                            v-col(:cols="12" :sm="6")
                                HaqSelect(
                                    :menu-props="selectMenuProps"
                                    v-model="form.sendDay"
                                    :items="dayOfMonthOptions"
                                    item-text="label"
                                    item-value="value"
                                    label="Day of Month"
                                    hide-details
                                )
                        v-row
                            v-col(:cols="12" :sm="6")
                                HaqSelect(
                                    :menu-props="selectMenuProps"
                                    v-model="form.timezone"
                                    :items="timezoneOptions"
                                    item-text="label"
                                    item-value="value"
                                    label="Timezone"
                                    hide-details
                                )
                    
                    //- Format Selection
                    .format-section
                        .section-label Format (select at least one)
                        v-checkbox(
                            v-model="form.formats"
                            label="Excel"
                            value="xlsx"
                            hide-details
                            dense
                        )
                        v-checkbox(
                            v-model="form.formats"
                            label="PDF"
                            value="pdf"
                            hide-details
                            dense
                        )
                    
                    .report-content-options
                        .section-label
                            | Report contents
                            v-tooltip(bottom max-width="320")
                                template(v-slot:activator="{ on, attrs }")
                                    v-btn(icon small aria-label="About report contents" v-bind="attrs" v-on="on")
                                        v-icon(small) mdi-help-circle-outline
                                span PDF and Excel include summary tables. Individual readings can be enabled separately for each format. PDF can also include charts.
                        v-checkbox(v-if="form.formats.includes('xlsx')" v-model="form.includeTrendDataExcel" label="Include individual readings in Excel" hide-details)
                        v-checkbox(v-if="form.formats.includes('pdf')" v-model="form.includeTrendDataPdf" label="Include individual readings in PDF" hide-details)
                        v-checkbox(v-if="form.formats.includes('pdf')" v-model="form.includeCharts" label="Include charts in PDF" hide-details)

                    //- Recipients Section
                    .recipients-section
                        .section-label Email Recipients
                        .selected-recipients(v-if="selectedRecipients.length > 0")
                            .selected-label {{ selectedRecipients.length }} selected
                            .selected-chips
                                v-chip(
                                    v-for="user in selectedRecipients"
                                    :key="user.id"
                                    small
                                    close
                                    close-icon="mdi-close"
                                    @click:close="toggleUser(user.id)"
                                ) {{ getRecipientLabel(user) }}
                        HaqTextField.mb-2(
                            v-model="userSearch"
                            label="Search"
                            placeholder="Search users..."
                            append-icon="mdi-magnify"
                            hide-details
                            clearable
                        )
                        .users-list(v-if="!loadingUsers")
                            .user-item(
                                v-for="user in filteredUsers"
                                :key="user.id"
                                @click="toggleUser(user.id)"
                            )
                                v-checkbox(
                                    :input-value="form.recipients.includes(user.id)"
                                    hide-details
                                    dense
                                    readonly
                                )
                                v-avatar.mr-2(size="32" color="primary")
                                    span.white--text.text-caption {{ getInitials(user) }}
                                .user-info
                                    .user-name {{ user.firstName }} {{ user.lastName }}
                                    .user-email {{ user.email }}
                        v-progress-circular(v-else indeterminate color="primary" size="24")
                        .no-users(v-if="!loadingUsers && filteredUsers.length === 0")
                            | No users found
                    
            v-card-actions
                v-spacer
                v-btn.haq-btn.haq-btn-secondary(@click="close") Cancel
                v-btn.haq-btn.haq-btn-primary(
                    :disabled="!canSave"
                    :loading="saving"
                    @click="save"
                ) {{ isEditing ? 'Update' : 'Schedule' }}
        v-dialog(v-model="pointSelectorOpen" content-class="haq-dialog multiMeasurementSelectorModal" scrollable max-width="1000")
            MultiMeasurementSelector(
                v-if="pointSelectorOpen"
                :reportSiteId="siteId"
                :preSelectedMeasurements="reportPoints"
                @addMeasurements="setReportPoints"
                @closeModal="pointSelectorOpen = false"
            )
</template>

<script>
import { HTTPClient } from '@/assets/js/http';
import Axios from 'axios';
import MultiMeasurementSelector from '@/components/util/MultiMeasurementSelector.vue';
import HaqSelect from '@/components/form/HaqSelect.vue';
import HaqTextField from '@/components/form/HaqTextField.vue';

const client = new HTTPClient();

export default {
    name: 'ScheduleReportModal',
    components: {
        MultiMeasurementSelector,
        HaqSelect,
        HaqTextField,
    },
    props: {
        reportTimezone: { type: String, default: 'America/Chicago' },
        reportName: { type: String, default: '' },
        value: {
            type: Boolean,
            default: false
        },
        siteId: {
            type: String,
            required: true
        },
        timeline: {
            type: String,
            default: 'lastWeek'
        },
        measurements: {
            type: Array,
            default: () => []
        },
        editingSchedule: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            formValid: false,
            saving: false,
            loadingContents: false,
            contentsError: '',
            reportPoints: [],
            pointSelectorOpen: false,
            contentsRequest: 0,
            selectMenuProps: { bottom: true, offsetY: true, closeOnClick: false, closeOnContentClick: false },
            sendingTest: false,
            loadingUsers: false,
            users: [],
            userSearch: '',
            form: {
                name: '',
                timeline: 'lastWeek',
                formats: ['xlsx'],
                includeTrendDataExcel: true,
                includeTrendDataPdf: false,
                includeCharts: false,
                recipients: [],
                isActive: true,
                frequency: 'daily',
                sendTime: '06:00',
                sendDay: null,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago'
            },
            timelineOptions: [
                { label: 'Yesterday', value: 'yesterday' },
                { label: 'Last Week', value: 'lastWeek' },
                { label: 'Last Month', value: 'lastMonth' }
            ],
            frequencyOptions: [
                { label: 'Daily', value: 'daily' },
                { label: 'Weekly', value: 'weekly' },
                { label: 'Monthly', value: 'monthly' }
            ],
            sendTimeOptions: Array.from({ length: 24 }, (_, i) => {
                const hour24 = String(i).padStart(2, '0') + ':00';
                const hour12 = i === 0 ? '12:00 AM'
                    : i < 12 ? i + ':00 AM'
                    : i === 12 ? '12:00 PM'
                    : (i - 12) + ':00 PM';
                return { label: hour12, value: hour24 };
            }),
            dayOfWeekOptions: [
                { label: 'Sunday', value: 0 },
                { label: 'Monday', value: 1 },
                { label: 'Tuesday', value: 2 },
                { label: 'Wednesday', value: 3 },
                { label: 'Thursday', value: 4 },
                { label: 'Friday', value: 5 },
                { label: 'Saturday', value: 6 }
            ],
            dayOfMonthOptions: Array.from({ length: 28 }, (_, i) => ({
                label: 'Day ' + (i + 1),
                value: i + 1
            })),
            timezoneOptions: [
                { label: 'Eastern (ET)', value: 'America/New_York' },
                { label: 'Central (CT)', value: 'America/Chicago' },
                { label: 'Mountain (MT)', value: 'America/Denver' },
                { label: 'Pacific (PT)', value: 'America/Los_Angeles' },
                { label: 'Alaska (AKT)', value: 'America/Anchorage' },
                { label: 'Hawaii (HT)', value: 'Pacific/Honolulu' }
            ]
        };
    },
    computed: {
        dialogOpen: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            }
        },
        isEditing() {
            return !!this.editingSchedule;
        },
        timelineLabel() {
            const option = this.timelineOptions.find(o => o.value === this.timeline);
            return option ? option.label : this.timeline;
        },
        selectedRecipients() {
            return this.form.recipients.map(id => {
                const user = this.users.find(u => u.id === id);
                return user || { id, firstName: 'Unknown', lastName: '', email: '' };
            });
        },
        filteredUsers() {
            if (!this.userSearch) return this.users;
            const search = this.userSearch.toLowerCase();
            return this.users.filter(u => 
                (u.firstName + ' ' + u.lastName).toLowerCase().includes(search) ||
                u.email.toLowerCase().includes(search)
            );
        },
        canSave() {
            return this.form.name && 
                   this.form.formats.length > 0 && 
                   this.form.recipients.length > 0 &&
                   this.reportPoints.length > 0;
        },
        /**
         * The open modal has the fields a test email needs.
         * @description Recipients are not required. The test goes to the signed-in user.
         * @returns {boolean}
         */
        canSendTest() {
            const timeline = this.isEditing ? this.form.timeline : this.timeline;
            const measurementCount = this.reportPoints.length;
            return !!(this.form.name && String(this.form.name).trim()
                && this.form.formats.length > 0
                && timeline
                && measurementCount > 0);
        }
    },
    watch: {
        value(newVal) {
            if (newVal) {
                this.initForm();
                this.loadUsers();
                this.loadContents();
            } else {
                this.pointSelectorOpen = false;
                this.contentsRequest++;
            }
        }
    },
    methods: {
        removePoint(id) {
            this.reportPoints = this.reportPoints.filter(point => point.id !== id);
        },
        setReportPoints(points) {
            // Unavailable saved points must be removed explicitly, not lost in the picker.
            const unavailable = this.reportPoints.filter(point => point.unavailable);
            this.reportPoints = points.map(point => ({ ...point })).concat(unavailable);
            this.pointSelectorOpen = false;
        },
        async loadContents() {
            const request = ++this.contentsRequest;
            this.contentsError = '';
            this.loadingContents = false;
            if (!this.isEditing) return;
            const scheduleId = this.editingSchedule.id;
            this.loadingContents = true;
            try {
                const res = await client.robustQuery(`/api/sites/${this.siteId}/scheduled-reports/${scheduleId}/contents`);
                if (request === this.contentsRequest) this.reportPoints = res.results || [];
            } catch (error) {
                if (request === this.contentsRequest) this.contentsError = 'Could not load report points.';
            } finally {
                if (request === this.contentsRequest) this.loadingContents = false;
            }
        },
        initForm() {
            const defaultTz = this.reportTimezone;
            this.pointSelectorOpen = false;
            this.reportPoints = this.isEditing
                ? (this.editingSchedule.measurements || []).map(id => ({ id, name: id }))
                : this.measurements.map(point => ({ ...point }));
            if (this.editingSchedule) {
                // Editing existing schedule
                this.form = {
                    name: this.editingSchedule.name || '',
                    timeline: this.editingSchedule.timeline || 'lastWeek',
                    formats: this.normalizeFormats(this.editingSchedule.formats),
                    includeTrendDataExcel: this.editingSchedule.includeTrendDataExcel !== false,
                    includeTrendDataPdf: this.editingSchedule.includeTrendDataPdf === true,
                    includeCharts: this.editingSchedule.includeCharts !== false,
                    recipients: (this.editingSchedule.recipients || []).slice(),
                    isActive: this.editingSchedule.isActive !== false,
                    frequency: this.editingSchedule.frequency || 'daily',
                    sendTime: this.editingSchedule.sendTime || '06:00',
                    sendDay: this.editingSchedule.sendDay != null ? this.editingSchedule.sendDay : null,
                    timezone: this.editingSchedule.timezone || defaultTz
                };
            } else {
                // Creating new schedule: Excel includes readings by default, PDF does not, charts ON
                this.form = {
                    name: this.reportName,
                    timeline: this.timeline,
                    formats: ['xlsx'],
                    includeTrendDataExcel: true,
                    includeTrendDataPdf: false,
                    includeCharts: true,
                    recipients: [],
                    isActive: true,
                    frequency: 'daily',
                    sendTime: '06:00',
                    sendDay: null,
                    timezone: defaultTz
                };
            }
        },
        /**
         * Map stored schedule formats onto current checkbox values (csv -> xlsx).
         * @description Treats legacy csv format as Excel so existing schedules keep the Excel checkbox.
         * @param {string[]} formats
         * @returns {string[]}
         */
        normalizeFormats(formats) {
            const list = Array.isArray(formats) && formats.length ? formats : ['xlsx'];
            return list.map(f => f === 'csv' ? 'xlsx' : f);
        },
        async loadUsers() {
            try {
                this.loadingUsers = true;
                const res = await client.robustQuery(`/api/sites/${this.siteId}/users`);
                this.users = res.results || [];
            } catch (error) {
                console.error('Failed to load users:', error);
                this.users = [];
            } finally {
                this.loadingUsers = false;
            }
        },
        getInitials(user) {
            const first = user.firstName ? user.firstName[0] : '';
            const last = user.lastName ? user.lastName[0] : '';
            return (first + last).toUpperCase() || '?';
        },
        getRecipientLabel(user) {
            const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
            return user.email ? `${name} (${user.email})` : (name || 'Unknown');
        },
        toggleUser(userId) {
            const index = this.form.recipients.indexOf(userId);
            if (index === -1) {
                this.form.recipients.push(userId);
            } else {
                this.form.recipients.splice(index, 1);
            }
        },
        close() {
            this.$emit('close');
        },
        /**
         * Modal settings that change the generated report.
         * @description Posted as body.override so the test matches the draft before any save.
         * @returns {object}
         */
        reportOverride() {
            const measurements = this.reportPoints.map(point => point.id);
            return {
                name: this.form.name,
                timeline: this.isEditing ? this.form.timeline : this.timeline,
                formats: this.form.formats.slice(),
                includeTrendDataExcel: this.form.includeTrendDataExcel,
                includeTrendDataPdf: this.form.includeTrendDataPdf,
                includeCharts: this.form.includeCharts,
                timezone: this.form.timezone,
                measurements
            };
        },
        /**
         * Send the modal draft to the signed-in user through the report mailer.
         * @description Posts body.override. A create draft has no saved id.
         */
        async sendTest() {
            if (!this.canSendTest || this.sendingTest) return;
            const scheduleId = this.isEditing && this.editingSchedule ? this.editingSchedule.id : '';
            const url = scheduleId
                ? `/api/sites/${this.siteId}/scheduled-reports/${scheduleId}/send-test`
                : `/api/sites/${this.siteId}/scheduled-reports/send-test`;
            try {
                this.sendingTest = true;
                await client.robustPost(url, { override: this.reportOverride() });
                window.M.toast({ html: 'Test report sent to your email' });
            } catch (error) {
                console.error('Failed to send test report:', error);
                window.M.toast({ html: error.message || 'Failed to send test report' });
            } finally {
                this.sendingTest = false;
            }
        },
        async save() {
            if (!this.canSave) return;
            
            try {
                this.saving = true;
                
                const payload = {
                    name: this.form.name,
                    timeline: this.isEditing ? this.form.timeline : this.timeline,
                    formats: this.form.formats,
                    includeTrendDataExcel: this.form.includeTrendDataExcel,
                    includeTrendDataPdf: this.form.includeTrendDataPdf,
                    includeCharts: this.form.includeCharts,
                    recipients: this.form.recipients,
                    isActive: this.form.isActive,
                    frequency: this.form.frequency,
                    sendTime: this.form.sendTime,
                    sendDay: this.form.sendDay,
                    timezone: this.form.timezone
                };
                
                payload.measurements = this.reportPoints.map(point => point.id);
                
                if (this.isEditing) {
                    // Update existing schedule using Axios directly for PATCH
                    await Axios.patch(`/api/sites/${this.siteId}/scheduled-reports/${this.editingSchedule.id}`, payload);
                } else {
                    // Create new schedule
                    await client.robustPost(`/api/sites/${this.siteId}/scheduled-reports`, payload);
                }
                
                this.$emit('saved');
            } catch (error) {
                console.error('Failed to save schedule:', error);
                window.M.toast({ html: 'Failed to save schedule' });
            } finally {
                this.saving = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.schedule-modal {
    .section-label {
        color: var(--app-primary-txt);
        font-size: 14px;
        font-weight: 500;
        margin-bottom: 8px;
    }
    
    .schedule-form {
        display: flex;
        flex-direction: column;
        gap: 20px;
    }
    .schedule-status {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        ::v-deep .v-input { margin: 0; padding: 0; }
    }
    .included-heading {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 8px;
        .section-label { margin: 0; }
    }
    .included-list {
        max-height: 180px;
        overflow: auto;
        border: 1px solid var(--border);
        border-radius: 8px;
    }
    .included-point {
        display: flex;
        flex-wrap: wrap;
        gap: 8px 16px;
        padding: 8px 12px;
        border-bottom: 1px solid var(--border);
        align-items: center;
        strong { flex: 1; }
        span { color: var(--app-tertiary-txt); }
    }
    .schedule-timing-section {
        margin-bottom: 0;
        
        .row { margin: 0 -8px; }
        .col-12 { padding: 8px; }
    }
    
    .format-section {
        margin-bottom: 0;
        
        .format-hint {
            display: block;
            color: var(--app-tertiary-txt);
            font-size: 12px;
            margin-top: 4px;
            margin-left: 32px;
        }
    }
    
    ::v-deep .haq-field__label { top: 0; margin-bottom: 8px; }
    ::v-deep .haq-field { padding-top: 0; }
    .recipients-section {
        margin-bottom: 0;
        
        .selected-recipients {
            margin-bottom: 12px;
            padding: 8px 12px;
            background-color: var(--app-tertiary-bg);
            border-radius: 8px;
            border: 1px solid var(--border);
            
            .selected-label {
                font-size: 12px;
                color: var(--app-tertiary-txt);
                margin-bottom: 8px;
            }
            
            .selected-chips {
                display: flex;
                flex-wrap: wrap;
                gap: 8px;
                
                .v-chip {
                    margin: 0;
                }
            }
        }
        
        .users-list {
            max-height: 200px;
            overflow-y: auto;
            border: 1px solid var(--border);
            border-radius: 8px;
            padding: 8px;
            background-color: var(--app-tertiary-bg);
        }
        
        .user-item {
            display: flex;
            align-items: center;
            padding: 8px;
            border-radius: 6px;
            cursor: pointer;
            transition: background-color 0.2s;
            
            &:hover {
                background-color: var(--app-secondary-bg);
            }
            
            .user-info {
                flex: 1;
                min-width: 0;
                overflow-wrap: anywhere;
                
                .user-name {
                    color: var(--app-primary-txt);
                    font-size: 14px;
                }
                
                .user-email {
                    color: var(--app-tertiary-txt);
                    font-size: 12px;
                }
            }
        }
        
        .no-users {
            text-align: center;
            color: var(--app-tertiary-txt);
            padding: 20px;
        }
    }
}
</style>
